import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Package, RefreshCcw, User, Shield, Bell, LifeBuoy, Database, ShieldAlert } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { AL, inputCls, errMsg } from "../components/account/shared";
import { OrdersList } from "../components/account/Orders";
import { Profile } from "../components/account/Profile";
import { SecurityPanel } from "../components/account/SecurityPanel";
import { Subscriptions } from "../components/account/Subscriptions";
import { Preferences, Support, DataPrivacy } from "../components/account/Misc";

const TwoFaFields = ({ l, code, setCode }) => (
  <>
    <p className="text-sm text-stone-600">{l.twofaHint}</p>
    <input data-testid="account-2fa-input" autoFocus required maxLength={6} className={`${inputCls} font-mono tracking-[0.4em] text-center text-lg`} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
  </>
);

const ForgotFields = ({ l, ta, form, setForm }) => (
  <>
    <p className="text-sm text-stone-600">{l.forgotBody}</p>
    <input data-testid="forgot-email" required type="email" className={inputCls} placeholder={ta.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
  </>
);

const CredentialFields = ({ mode, ta, l, form, setForm }) => (
  <>
    {mode === "register" && <input data-testid="account-name-input" required className={inputCls} placeholder={ta.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
    <input data-testid="account-email-input" required type="email" className={inputCls} placeholder={ta.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
    <input data-testid="account-password-input" required type="password" minLength={8} className={inputCls} placeholder={ta.password} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
    {mode === "register" && <label className="flex items-center gap-2 text-sm text-stone-600"><input data-testid="account-newsletter" type="checkbox" checked={form.newsletter} onChange={(e) => setForm({ ...form, newsletter: e.target.checked })} className="accent-[#C85A32] w-4 h-4" /> {l.newsletter}</label>}
  </>
);


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [["commandes", "orders", Package], ["paiement", "subs", RefreshCcw], ["profil", "profile", User], ["securite", "security", Shield], ["preferences", "prefs", Bell], ["contact", "support", LifeBuoy], ["donnees", "data", Database]];

const AuthForm = ({ ta, l, login, register, verify2fa }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", newsletter: false });
  const [busy, setBusy] = useState(false);
  const [pre, setPre] = useState(null);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "forgot") { await axios.post(`${API}/auth/forgot-password`, { email: form.email }); setSent(true); return; }
      if (pre) { await verify2fa(pre, code); toast.success(ta.welcome); return; }
      const data = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password, form.newsletter);
      if (data.requires_2fa) { setPre(data.pre_token); return; }
      toast.success(ta.welcome);
    } catch (err) { toast.error(errMsg(err, "Erreur — vérifiez vos informations.")); } finally { setBusy(false); }
  };

  const view = pre ? "twofa" : mode;
  const TITLES = { twofa: l.twofa, forgot: l.forgotTitle, login: ta.login, register: ta.register };
  return (
    <div className="min-h-screen pt-32 pb-24 px-6 flex justify-center" data-testid="account-auth-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-3">{ta.title}</p>
        <h1 className="font-serif text-4xl text-stone-900 mb-8">{TITLES[view]}</h1>
        {!pre && mode !== "forgot" && (
          <div className="flex rounded-full border border-stone-300 p-1 mb-8 text-[11px] uppercase tracking-[0.15em]">
            {["login", "register"].map((m) => <button key={m} data-testid={`account-mode-${m}`} onClick={() => setMode(m)} className={`flex-1 py-2.5 rounded-full transition-all duration-300 ${mode === m ? "bg-stone-900 text-white" : "text-stone-500"}`}>{m === "login" ? ta.login : ta.register}</button>)}
          </div>
        )}
        {!pre && mode !== "forgot" && (
          <>
            <button type="button" data-testid="google-signin" onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/compte";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }} className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-stone-300 bg-white py-3.5 text-[12px] uppercase tracking-[0.15em] text-stone-800 hover:border-stone-900 transition-colors">
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.7 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.7-2 15.5-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-8 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
              {l.google}
            </button>
            <div className="my-6 flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.3em] text-stone-400"><span className="flex-1 h-px bg-stone-200" />{l.or}<span className="flex-1 h-px bg-stone-200" /></div>
          </>
        )}
        <form onSubmit={submit} className="space-y-4">
          {view === "twofa" && <TwoFaFields l={l} code={code} setCode={setCode} />}
          {view === "forgot" && (sent ? <p className="text-sm text-stone-700" data-testid="forgot-sent">{l.sentMsg}</p> : <ForgotFields l={l} ta={ta} form={form} setForm={setForm} />)}
          {(view === "login" || view === "register") && <CredentialFields mode={mode} ta={ta} l={l} form={form} setForm={setForm} />}
          {!(mode === "forgot" && sent) && <button data-testid="account-submit-button" type="submit" disabled={busy} className="w-full rounded-full bg-stone-900 text-[#FAF7F2] py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300 disabled:opacity-60">{busy ? "..." : pre ? l.verify : mode === "forgot" ? l.send : mode === "login" ? ta.loginCta : ta.registerCta}</button>}
        </form>
        <div className="mt-6 flex justify-between text-xs text-stone-500">
          {mode === "login" && !pre && <button data-testid="account-forgot" onClick={() => setMode("forgot")} className="underline">{l.forgot}</button>}
          {(mode === "forgot" || pre) && <button data-testid="account-back" onClick={() => { setMode("login"); setPre(null); setSent(false); }} className="underline">{l.back}</button>}
          <span>{mode === "login" && ta.hint}</span>
        </div>
      </motion.div>
    </div>
  );
};

export default function AccountPage() {
  const { user, login, register, logout, verify2fa, refreshUser } = useAuth();
  const { t, lang, config } = useStore();
  const ta = t.account;
  const L = AL[lang];
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "commandes";
  const [orders, setOrders] = useState(null);
  const [myReviews, setMyReviews] = useState([]);

  const reload = useCallback(() => {
    axios.get(`${API}/my/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
    axios.get(`${API}/my/reviews`, { withCredentials: true }).then((r) => setMyReviews(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user && user.role) reload(); }, [user, reload]);

  if (user === null) return <div className="min-h-screen pt-40 text-center text-stone-500 font-mono text-sm">…</div>;
  if (!user) return <AuthForm ta={ta} l={L.login} login={login} register={register} verify2fa={verify2fa} />;
  const go = (id) => setParams({ tab: id });

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 lg:px-14 xl:px-24 max-w-[1300px] mx-auto" data-testid="account-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-3">{ta.title}</p>
          <h1 className="font-serif text-4xl text-stone-900">{ta.hello} {user.name}</h1>
          <p className="mt-1 text-sm text-stone-500 font-mono">{user.email}</p>
        </div>
        <button data-testid="account-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-stone-600 hover:bg-stone-900 hover:text-white transition-colors duration-300"><LogOut size={13} /> {ta.logout}</button>
      </div>
      {config.customer_2fa_prompt && !user.twofa_enabled && tab !== "securite" && (
        <button data-testid="account-2fa-banner" onClick={() => go("securite")} className="mb-8 w-full text-left rounded-2xl border border-[#C85A32]/30 bg-[#FDF6F0] px-5 py-3.5 text-sm text-stone-800 flex items-center gap-3 hover:border-[#C85A32] transition-colors"><ShieldAlert size={16} className="text-[#C85A32]" /> {L.promo2fa}</button>
      )}
      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-6 px-6 lg:mx-0 lg:px-0" data-testid="account-tabs">
          {TABS.map(([id, key, Icon]) => (
            <button key={id} data-testid={`account-tab-${id}`} onClick={() => go(id)} className={`flex items-center gap-3 rounded-xl px-4 py-3 text-[11px] uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${tab === id ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-white"}`}><Icon size={14} /> {L.tabs[key]}</button>
          ))}
        </nav>
        <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="min-w-0">
          {tab === "commandes" && <OrdersList orders={orders} t={t} lang={lang} myReviews={myReviews} reload={reload} />}
          {tab === "paiement" && <Subscriptions l={L.subs} lang={lang} />}
          {tab === "profil" && <Profile user={user} refreshUser={refreshUser} l={L.profile} />}
          {tab === "securite" && <SecurityPanel user={user} refreshUser={refreshUser} l={L.security} />}
          {tab === "preferences" && <Preferences l={L.prefs} lang={lang} />}
          {tab === "contact" && <Support l={L.support} orders={orders} config={config} />}
          {tab === "donnees" && <DataPrivacy l={L.data} logout={logout} />}
        </motion.div>
      </div>
    </div>
  );
}
